import { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { CredentialFormFields } from "@/components/org/credentials/CredentialFormFields";

export const CredentialEditModal = ({ credential, meta, onClose, onSaved }) => {
  const { API } = useAuth();
  const [form, setForm] = useState({
    credential_type: credential.credential_type,
    credential_name: credential.credential_name || "",
    description: credential.description || "",
    credential_number: credential.credential_number || "",
    issuer: credential.issuer || "",
    holder_type: credential.holder_type,
    holder_id: credential.holder_id || null,
    issue_date: credential.issue_date || "",
    expiration_date: credential.expiration_date || "",
    does_not_expire: !!credential.does_not_expire,
    scope_type: credential.scope_type || null,
    scope_reference: credential.scope_reference || null,
    notes: credential.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!form.credential_name.trim()) {
      setError("Credential name is required.");
      return;
    }
    if (form.holder_type === "person" && !form.holder_id) {
      setError("Please select a person for this credential.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        credential_name: form.credential_name.trim(),
        description: form.description.trim() || null,
        credential_number: form.credential_number.trim() || null,
        issuer: form.issuer.trim() || null,
        issue_date: form.issue_date || null,
        expiration_date: form.does_not_expire ? null : (form.expiration_date || null),
        notes: form.notes.trim() || null,
      };
      const { data } = await axios.patch(`${API}/org/credentials/${credential.credential_id}`, payload, { withCredentials: true });
      onSaved(data.credential);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save this credential.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="credential-edit-overlay">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="credential-edit-modal">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Edit Credential</h2>
          <button type="button" onClick={onClose} data-testid="credential-edit-close-btn" className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4">
          <CredentialFormFields form={form} setForm={setForm} meta={meta} testIdPrefix="credential-edit" />
        </div>
        {error && <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="credential-edit-error">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="credential-edit-cancel-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="credential-edit-save-btn" className="rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
};
